import Form from 'antd/lib/form';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import { useDispatch, useSelector } from 'react-redux';
import { AppComponentProps } from '@/typescriptHelpers';
import Checkbox from 'antd/lib/checkbox';
import Select from 'antd/lib/select';
import { AppSettingsUpdatable } from '@/actions/updatable';
import { UpdateFixesFeatureSettingsRequest, tryUpdateAppSettings } from '@/actions/appSettings';
import { selectAddFixRequestsToOpenPRsAgainstReleaseBranches, selectAppSettings } from '@/selectors/selectors';
import { RootState } from 'reducers/reducers';
import {
    AutoCherryPickMatchTokenOn,
    FixesTokenConfig,
    FixesTokenSettings,
    ReleaseTypesForNonBlockingSteps,
} from '@/types';
import { cherryPickPatternValidator } from '@/helpers';
import { FormInstance, useWatch } from 'antd/es/form/Form';
import { cloneDeep, flatMap, fromPairs } from 'lodash';
import { ReleaseTypeTab, ReleaseTypeTabs, groupReleaseTypesByValue } from '../ReleaseTypeTabs';
import { antGrey } from '../styles/globalStyles';
import { useFixSettingsModal } from './useFixSettingsModal';
import FixSettingsDrawer from './FixSettingsDrawer';

const FIXES_TOKEN_CONFIGS_FIELD = 'fixesTokenConfigs';

export const fixesTokenSettingsForReleaseType = (
    config: FixesTokenConfig | undefined,
    releaseType: ReleaseTypesForNonBlockingSteps,
): FixesTokenSettings => {
    const settings = config?.configPerReleaseType?.[releaseType] ?? config;
    return {
        enabled: settings?.enabled ?? false,
        fixRequestTokens: settings?.fixRequestTokens ?? ['[runway:fix-{version}]'],
        matchTokenOn: settings?.matchTokenOn ?? [AutoCherryPickMatchTokenOn.Title],
    };
};

const initialFixesTokenTabs = (config: FixesTokenConfig | undefined) =>
    groupReleaseTypesByValue((releaseType) => fixesTokenSettingsForReleaseType(config, releaseType)).map(
        (group, index) => ({ tab: { id: `tab-${index}`, releaseTypes: group.releaseTypes }, settings: group.value }),
    );

const FixesTokenFields: React.FC<{ tabId: string; form: FormInstance }> = ({ tabId, form }) => {
    const isEnabled = useWatch([FIXES_TOKEN_CONFIGS_FIELD, tabId, 'enabled'], form);

    return (
        <>
            <Form.Item
                name={[FIXES_TOKEN_CONFIGS_FIELD, tabId, 'enabled']}
                valuePropName="checked"
                style={{ marginBottom: 4 }}
            >
                <Checkbox>Allow PRs to be flagged as fixes using a token</Checkbox>
            </Form.Item>
            <Form.Item
                name={[FIXES_TOKEN_CONFIGS_FIELD, tabId, 'fixRequestTokens']}
                label="Fix token patterns"
                style={{ marginLeft: 24 }}
                tooltip="Include a string to flag a PR as a fix. Pattern must include {version}, or {versionConcise}, or no tokens at all."
                rules={[
                    { required: isEnabled },
                    {
                        validator: (_, value) => (isEnabled ? cherryPickPatternValidator(value) : Promise.resolve()),
                    },
                ]}
            >
                <Select
                    style={{ width: '100%' }}
                    allowClear
                    mode="tags"
                    notFoundContent={null}
                    placeholder="Select"
                    disabled={!isEnabled}
                />
            </Form.Item>
            <Form.Item
                name={[FIXES_TOKEN_CONFIGS_FIELD, tabId, 'matchTokenOn']}
                label="Match token on..."
                style={{ marginLeft: 24, marginBottom: 0 }}
                rules={[{ required: isEnabled }]}
            >
                <Select
                    style={{ width: '100%' }}
                    allowClear
                    showSearch={false}
                    mode="multiple"
                    placeholder="Select"
                    disabled={!isEnabled}
                    options={[
                        { value: AutoCherryPickMatchTokenOn.Title, label: 'PR title' },
                        { value: AutoCherryPickMatchTokenOn.Label, label: 'PR label' },
                    ]}
                />
            </Form.Item>
        </>
    );
};

interface EditOpenPRFixesModalProps extends AppComponentProps {
    open: boolean;
    onCancel: () => void;
}

const EditOpenPRFixesModal: React.FC<EditOpenPRFixesModalProps> = (props: EditOpenPRFixesModalProps) => {
    const { form, isLoading, afterClose } = useFixSettingsModal({ open: props.open, onCancel: props.onCancel });
    const dispatch = useDispatch();

    const addFixRequestsToOpenPRsAgainstReleaseBranches = useSelector(
        selectAddFixRequestsToOpenPRsAgainstReleaseBranches(props.appId),
    );
    const appSettings = useSelector((state: RootState) => selectAppSettings(state, props.appId));
    const fixesTokenConfigSnapshot = JSON.stringify(appSettings.fixesTokenConfig ?? null);
    const initialTokenTabs = React.useMemo(
        () => initialFixesTokenTabs(JSON.parse(fixesTokenConfigSnapshot) ?? undefined),
        [fixesTokenConfigSnapshot],
    );
    const [tokenTabs, setTokenTabs] = React.useState<ReleaseTypeTab[]>(() => initialTokenTabs.map((t) => t.tab));
    const [activeTokenTabId, setActiveTokenTabId] = React.useState('tab-0');
    const [tokenTabsKey, setTokenTabsKey] = React.useState(0);

    React.useEffect(() => {
        if (!props.open) return;
        setTokenTabs(initialTokenTabs.map((t) => t.tab));
        setActiveTokenTabId('tab-0');
        setTokenTabsKey((key) => key + 1);
    }, [props.open, initialTokenTabs]);
    const initialValues = {
        addFixRequestsToOpenPRsAgainstReleaseBranches: addFixRequestsToOpenPRsAgainstReleaseBranches,
        [FIXES_TOKEN_CONFIGS_FIELD]: fromPairs(initialTokenTabs.map(({ tab, settings }) => [tab.id, settings])),
    };

    const buildFixesTokenConfig = (settingsByTabId: Record<string, FixesTokenSettings>): FixesTokenConfig => {
        const [defaultTab, ...overrideTabs] = tokenTabs;
        return {
            ...settingsByTabId[defaultTab.id],
            configPerReleaseType: fromPairs(
                flatMap(overrideTabs, (tab) =>
                    tab.releaseTypes.map((releaseType) => [releaseType, settingsByTabId[tab.id]]),
                ),
            ),
        };
    };

    const onFinishFailed = ({ errorFields }: { errorFields: { name: (string | number)[] }[] }) => {
        const tokenTabError = errorFields.find(({ name }) => name[0] === FIXES_TOKEN_CONFIGS_FIELD);
        if (tokenTabError) {
            setActiveTokenTabId(String(tokenTabError.name[1]));
        }
    };

    const onFinish = (value) => {
        const request: UpdateFixesFeatureSettingsRequest = {
            appId: props.appId,
            orgId: props.orgId,
            addFixRequestsToOpenPRsAgainstReleaseBranches: value.addFixRequestsToOpenPRsAgainstReleaseBranches,
            fixesTokenConfig: buildFixesTokenConfig(value[FIXES_TOKEN_CONFIGS_FIELD]),
        };
        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.RequiresFixRequestsApprovals, request));
    };

    return (
        <FixSettingsDrawer
            title="Fix creation"
            open={props.open}
            loading={isLoading}
            onSave={form.submit}
            onCancel={props.onCancel}
            afterClose={afterClose}
        >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Text style={{ color: antGrey.grey7 }}>
                    Configure when Runway automatically creates fixes from pull requests.
                </Text>
                <Form
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                    initialValues={initialValues}
                    style={{ marginTop: 24, width: '100%' }}
                    form={form}
                    layout="vertical"
                >
                    <Form.Item name="addFixRequestsToOpenPRsAgainstReleaseBranches" valuePropName="checked">
                        <Checkbox>Create a fix for all open PRs against the release branch</Checkbox>
                    </Form.Item>
                    <Form.Item label="Fix tokens">
                        <ReleaseTypeTabs
                            key={tokenTabsKey}
                            tabs={tokenTabs}
                            onTabsChange={setTokenTabs}
                            activeTabId={activeTokenTabId}
                            onActiveTabChange={setActiveTokenTabId}
                            onTabSplit={(sourceTabId, newTabId) =>
                                form.setFieldValue(
                                    [FIXES_TOKEN_CONFIGS_FIELD, newTabId],
                                    cloneDeep(form.getFieldValue([FIXES_TOKEN_CONFIGS_FIELD, sourceTabId])),
                                )
                            }
                            renderTabContent={(tab) => <FixesTokenFields tabId={tab.id} form={form} />}
                        />
                    </Form.Item>
                </Form>
            </div>
        </FixSettingsDrawer>
    );
};

export default EditOpenPRFixesModal;
